import {
  Box,
  Button,
  Flex,
  Link,
  Text,
  Image,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
} from "@chakra-ui/react";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  DownloadIcon,
  LinkIcon,
  LockIcon,
  PhoneIcon,
  RepeatClockIcon,
  StarIcon,
} from "@chakra-ui/icons";
import { IntegrationService } from "../../service/integration.service";
import { useDispatch } from "react-redux";
import { update as tokenUpdate } from "../../store/slice/Token/token.slice";
import { MagiclinkService } from "../../service/third-party/magiclink.service";
import { updateSidebar } from "../../store/slice/Sidebar/sidebar.slice";
import { store } from "../../store";
import styles from "../../styles/Home.module.css";
import { colors } from "../../constants/design.constants";

const integrationService = new IntegrationService();
const magiclinkService = new MagiclinkService();

export const Sidebar: React.FC<any> = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { logoSrc, workflows } = store.getState().sidebar;

  const logout = async () => {
    dispatch(tokenUpdate({}));
    dispatch(updateSidebar({ logoSrc: "", workflows: [] }));
    await magiclinkService.logout();
    navigate("/auth/app/login");
  };

  const init = async () => {
    const workflows = await integrationService.getWorkflows();
    const email = await magiclinkService.userEmail();

    const company = email.split("@")[1].split(".")[0];
    const src = `/assets/company-logo/${company.toLocaleLowerCase()}.png`;

    dispatch(
      updateSidebar({
        logoSrc: src,
        workflows: workflows.map((x) => ({
          id: x.id,
          friendlyName: x.friendlyName,
          description: x.workflowDescription,
        })),
      })
    );
  };

  useEffect(() => {
    if (!workflows?.length) {
      init();
    }
  }, []);

  return (
    <Box
      maxW="260px"
      minH="100%"
      display={{ base: "none", lg: "block" }}
      position="fixed"
      zIndex={40}
    >
      <Flex
        minH="100vh"
        bg={colors.void}
        w="260px"
        borderRight={`1px solid ${colors.border}`}
        p="0"
        flexDirection="column"
        justifyContent="space-between"
      >
        <Box>
          <Box
            px="20px"
            pt="28px"
            pb="20px"
            borderBottom={`1px solid ${colors.borderSubtle}`}
          >
            <Text
              fontSize="10px"
              fontWeight="500"
              letterSpacing="0.16em"
              textTransform="uppercase"
              color={colors.signal}
              mb="10px"
            >
              Lighthouse
            </Text>
            <a onClick={() => navigate("/dashboard")}>
              {logoSrc ? (
                <Box
                  px="12px"
                  py="10px"
                  borderRadius="2px"
                  display="inline-block"
                  cursor="pointer"
                  transition="opacity 0.2s"
                  _hover={{ opacity: 0.9 }}
                >
                  <Image
                    src={logoSrc}
                    alt="Company Logo"
                    width="128px"
                    display="block"
                  />
                </Box>
              ) : (
                <Text
                  fontSize="15px"
                  fontWeight="600"
                  letterSpacing="-0.02em"
                  color={colors.textPrimary}
                  cursor="pointer"
                >
                  SUMLOOKUP
                </Text>
              )}
            </a>
          </Box>

          <Box px="12px" pt="20px">
            <Text
              px="12px"
              mb="8px"
              fontSize="10px"
              fontWeight="500"
              letterSpacing="0.14em"
              textTransform="uppercase"
              color={colors.textTertiary}
            >
              Operations
            </Text>

            <LinkStyled
              active={window.location.pathname.startsWith(`/dashboard`)}
              onClick={() => navigate(`/dashboard`)}
            >
              <Flex flexDirection="row" gap={2.5} alignItems="center">
                <Icon link="dashboard" />
                <Text>Dashboard</Text>
              </Flex>
            </LinkStyled>

            <LinkStyled
              active={window.location.pathname.startsWith(`/integrations`)}
              onClick={() => navigate(`/integrations`)}
            >
              <Flex flexDirection="row" gap={2.5} alignItems="center">
                <Icon link="integrations" />
                <Text>Manual Trigger</Text>
              </Flex>
            </LinkStyled>

            <LinkStyled
              active={window.location.pathname.startsWith(`/archive`)}
              onClick={() => navigate(`/archive`)}
            >
              <Flex flexDirection="row" gap={2.5} alignItems="center">
                <Icon link="archive" />
                <Text>Archive & Logs</Text>
              </Flex>
            </LinkStyled>

            <Accordion allowToggle defaultIndex={[0]} mt="4px">
              <AccordionItem border="none">
                <AccordionButton
                  borderRadius="2px"
                  py="10px"
                  px="12px"
                  fontSize="13px"
                  color={
                    window.location.pathname.startsWith("/history")
                      ? colors.textPrimary
                      : colors.textSecondary
                  }
                  bg={
                    window.location.pathname.startsWith("/history")
                      ? colors.hover
                      : "transparent"
                  }
                  borderLeft={
                    window.location.pathname.startsWith("/history")
                      ? `2px solid ${colors.signal}`
                      : "2px solid transparent"
                  }
                  _hover={{ bg: colors.hover, color: colors.textPrimary }}
                  _expanded={{ color: colors.textPrimary }}
                >
                  <Flex
                    fontWeight="500"
                    flexDir="row"
                    gap="10px"
                    alignItems="center"
                    flex="1"
                  >
                    <Icon link="history" />
                    History
                  </Flex>
                  <AccordionIcon color={colors.textTertiary} />
                </AccordionButton>
                <AccordionPanel className={styles["scroll-container"]} px="0" pb="0">
                  {workflows?.map((workflow, i) => (
                    <SublinkStyled
                      active={window.location.pathname.startsWith(
                        `/history/${workflow.id}`
                      )}
                      key={i}
                      onClick={() => navigate(`/history/${workflow.id}`)}
                    >
                      <Flex flexDirection="row" gap={2} alignItems="center">
                        <Box
                          w="4px"
                          h="4px"
                          borderRadius="0"
                          bg={
                            window.location.pathname.startsWith(
                              `/history/${workflow.id}`
                            )
                              ? colors.signal
                              : colors.textTertiary
                          }
                          flexShrink={0}
                        />
                        <Text fontWeight="400" fontSize="12px" noOfLines={1}>
                          {workflow.friendlyName}
                        </Text>
                      </Flex>
                    </SublinkStyled>
                  ))}
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          </Box>
        </Box>

        <Box px="12px" pb="16px" borderTop={`1px solid ${colors.borderSubtle}`} pt="12px">
          <LinkStyled
            active={window.location.pathname.startsWith(`/support`)}
            onClick={() => navigate(`/support`)}
          >
            <Flex flexDirection="row" gap={2.5} alignItems="center">
              <Icon link="support" />
              <Text>Support</Text>
            </Flex>
          </LinkStyled>

          <LinkStyled onClick={logout}>
            <Flex flexDirection="row" gap={2.5} alignItems="center">
              <Link as={LockIcon} boxSize="14px" />
              <Text>End Session</Text>
            </Flex>
          </LinkStyled>
        </Box>
      </Flex>
    </Box>
  );
};

const LinkStyled: React.FC<any> = (props: any) => {
  const { children, active, onClick } = props;

  return (
    <Button
      onClick={onClick}
      fontSize="13px"
      fontWeight="500"
      boxSize="initial"
      justifyContent="flex-start"
      alignItems="center"
      bg={active ? colors.hover : "transparent"}
      mb="2px"
      pl="12px"
      pr="12px"
      _hover={{
        bg: colors.hover,
        color: colors.textPrimary,
      }}
      py="10px"
      color={active ? colors.textPrimary : colors.textSecondary}
      borderRadius="2px"
      borderLeft={active ? `2px solid ${colors.signal}` : "2px solid transparent"}
      w="100%"
      transition="all 0.15s ease"
    >
      {children}
    </Button>
  );
};

const SublinkStyled: React.FC<any> = (props: any) => {
  const { children, active, onClick } = props;

  return (
    <Button
      onClick={onClick}
      fontSize="12px"
      fontWeight="400"
      boxSize="initial"
      justifyContent="flex-start"
      alignItems="center"
      bg={active ? colors.signalMuted : "transparent"}
      mb="1px"
      ml="8px"
      pl="12px"
      pr="8px"
      _hover={{
        bg: colors.hover,
        color: colors.textPrimary,
      }}
      py="8px"
      color={active ? colors.signal : colors.textSecondary}
      borderRadius="2px"
      w="calc(100% - 8px)"
      transition="all 0.15s ease"
    >
      {children}
    </Button>
  );
};

const Icon: React.FC<any> = (props: any) => {
  const { link } = props;
  const size = "14px";

  if (link === "integrations") return <Link as={LinkIcon} boxSize={size} />;
  if (link === "history") return <Link as={RepeatClockIcon} boxSize={size} />;
  if (link === "dashboard") return <Link as={StarIcon} boxSize={size} />;
  if (link === "archive") return <Link as={DownloadIcon} boxSize={size} />;
  if (link === "support") return <Link as={PhoneIcon} boxSize={size} />;
  return null;
};
